// src/components/vault/ui/VaultPageRedesign.tsx
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { validToUpload } from "../fixtures/validToUpload";
import { Menu } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  isBdcActorMismatch,
  decodeVaultJwtActor,
  isNativeBdcSupported,
} from "../../../utils/vault/bdcActor";
import {
  Folder,
  Requirement,
  Status,
  Subfolder,
  Type,
} from "../../../redux/interfaces/folder.types";
import {
  setActiveVaultTab,
  setActiveRequirement,
  clearActiveRequirement,
} from "../../../redux/slices/vaultSlice";
import { documentationMap, DocumentationMapOptions } from "../documentationMap";
import { getSubfoldersMapByIdToSlug } from "../../../utils/subfolders/subfoldersMapByIdToSlug";
import { subfoldersMapping } from "../fixtures/subfoldersMapping";
import { getTabConfig } from "../../../config/vault/tabRequirements";

import { VaultLayout } from "./VaultLayout";
import { VaultSidebar } from "./VaultSidebar";
import { VaultTopNav } from "./VaultTopNav";
import {
  VaultRequirementContent,
  VaultEmptyState,
} from "./VaultRequirementContent";
import { Button } from "../../ui/button";
import { TabStepContent } from "./transitions/AnimatedContent";
import { AutoSaveProvider } from "../../../contexts/AutoSaveContext";
import { useVerificationBlocker } from "../../../hooks/vault/useVerificationBlocker";
import { VerificationBlockerModal } from "./VerificationBlockerModal";
import {
  createBdcCompanyDataRequirement,
  canCaptureBdcIdentity,
  getBdcSicSource,
  BDC_CONTACT_REVIEW_SLUG,
  DATA_VALIDATION_SLUG,
  hasValidatedPersonForm,
  hasVerifiedExistingBdcRun,
  isBdcReportReplacementPending,
  getRequirementGroup,
  isIdentityRequirement,
  isProofOfAddressRequirement,
  isProofOfAddressProcessing,
  isBdcCompanyDataRequirement,
  isRequirementLockedByPrerequisites,
  isRequirementVisuallyComplete,
  shouldKeepProofOfAddressActive,
  isBdcAuthorizationRequirement,
  sortRequirementsByGroup,
} from "../utils/requirementGrouping";
import { DataValidation } from "../documents/DataValidation";
import { BdcContactReview } from "../documents/bdc/BdcContactReview";
import {
  hasReviewedNipDestination,
  hasValidIDData,
  isBdcNaturalPersonSubfolder,
  isDataValidationSubmittedWorkflowStep,
} from "../documents/id-validation/idDataValidation";
import { MobileNavBar } from "./MobileNavBar";
import { MobileStepSheet } from "./MobileStepSheet";
import { useSwipeNavigation } from "../../../hooks/useSwipeNavigation";
import { useBreakpoint } from "../../../hooks/useIsMobile";
import { useHaptics } from "../../../hooks/useHaptics";
import { useSingleFlight } from "../../../hooks/useSingleFlight";
import { useBdcStatus } from "../../../hooks/vault/queries/useBdcStatus";
import { useInvalidateVaultFolder } from "../../../hooks/vault/queries/useVaultFolder";
import { motion } from "motion/react";
import { isIdentityRequirementComplete } from "../../../utils/vault/isIdentityRequirementComplete";
import { VaultRequirementScope } from "./VaultRequirementScope";
import {
  hasSettledPersistedIdentityValidation,
  isIdentityValidationActivityProcessing,
  type IdentityValidationActivity,
  type IdentityValidationActivityUpdate,
} from "../identityValidationActivity";

interface VaultPageRedesignProps {
  folder: Folder;
}

function isTrustedCrossValidatedProofOfAddress(
  proofOfAddress: Requirement | undefined,
  identity: Requirement | undefined,
): boolean {
  const verification = proofOfAddress?.verification_data;
  const address = verification?.address_from_identity;
  return Boolean(
    proofOfAddress?.status === Status.PendingReview &&
      proofOfAddress.workflow_step === "completed" &&
      identity?.id &&
      verification?.cross_validated_from_identity === true &&
      String(verification.identity_requirement_id) === String(identity.id) &&
      verification.validation_method === "user_confirmed_address_updated" &&
      typeof verification.validated_at === "string" &&
      verification.validated_at.length > 0 &&
      address &&
      typeof address === "object" &&
      !Array.isArray(address) &&
      Object.keys(address).length > 0,
  );
}

// Contextual descriptions for each subfolder type
const subfolderDescriptions: Record<string, string> = {
  // Tenant documents
  "tenant-pf":
    "Documentos e información personal del inquilino para validar su solicitud.",
  "tenant-pm": "Documentos de la empresa inquilina y su representante legal.",
  "tenant-pf-cja":
    "Documentos e información personal del inquilino para validar su solicitud.",
  "tenant-pm-cja":
    "Documentos de la empresa inquilina y su representante legal.",
  // Guarantor documents
  "guarantor-pf":
    "Documentos del fiador que respalda la solicitud de arrendamiento.",
  "guarantor-pf-cja":
    "Documentos del fiador que respalda la solicitud de arrendamiento.",
  "guarantor-pm": "Documentos de la empresa fiadora y su representante legal.",
  "guarantor-pm-cja":
    "Documentos de la empresa fiadora y su representante legal.",
  "guarantor-investigation-pf": "Documentos del fiador para la investigación.",
  "guarantor-investigation-pm":
    "Documentos de la empresa fiadora para la investigación.",
  // Lease contract
  "lease-contract":
    "Información del inmueble y datos para generar el contrato.",
  "lease-contract-pf":
    "Información del inmueble y datos para generar el contrato.",
  "lease-contract-pm":
    "Información del inmueble y datos para generar el contrato.",
  // Others
  collateral: "Documentos del inmueble que se ofrece como garantía.",
  "owner-representative": "Documentos del representante del propietario.",
  "owner-representative-pm": "Documentos del representante del propietario.",
  "landlord-pf": "Documentos e información del propietario.",
  "landlord-pm": "Documentos e información del propietario.",
};

// Order priority for subfolders (lower = first). Principal vs additional participants
// are distinguished by slug: principals use the base slugs, additional tenants/guarantors
// use the "-cja" variants, and secondary landlords use the "landlord-*" templates.
const subfolderOrder: Record<string, number> = {
  // 1) Principal tenant
  "tenant-pf": 1,
  "tenant-pm": 1,
  // 2) Additional tenants
  "tenant-pf-cja": 2,
  "tenant-pm-cja": 2,
  // 3) Principal guarantor
  "guarantor-pf": 3,
  "guarantor-pm": 3,
  "guarantor-investigation-pf": 3,
  "guarantor-investigation-pm": 3,
  // 4) Additional guarantors
  "guarantor-pf-cja": 4,
  "guarantor-pm-cja": 4,
  // 5) Collateral
  collateral: 5,
  // 6) Lease contract (principal landlord + rental data)
  "lease-contract": 6,
  "lease-contract-pf": 6,
  "lease-contract-pm": 6,
  // 7) Secondary landlords
  "landlord-pf": 7,
  "landlord-pm": 7,
  // 8) Owner representative
  "owner-representative": 8,
  "owner-representative-pm": 8,
};

export function VaultPageRedesign({ folder }: VaultPageRedesignProps) {
  const dispatch = useAppDispatch();
  const invalidateVaultFolder = useInvalidateVaultFolder(folder?.id);
  const {
    activeTab,
    activeRequirement: storeActiveRequirement,
    jwtToken,
  } = useAppSelector((state) => state.vault);
  const [isStepSheetOpen, setIsStepSheetOpen] = useState(false);
  const { isMobile } = useBreakpoint();
  const { trigger: hapticTrigger } = useHaptics();

  // Ref for scrolling content to top on tab change
  const contentScrollRef = useRef<HTMLDivElement>(null);

  // Section banner shown on cross-tab navigation
  const [sectionBanner, setSectionBanner] = useState<{
    name: string;
    description: string;
    stepCount: number;
  } | null>(null);

  // Track navigation direction for animations (1 = forward, -1 = backward)
  const [navigationDirection, setNavigationDirection] = useState(1);
  const previousRequirementIndex = useRef<number>(0);

  // Track if ID/POA validation flow is complete (internal UI state, not backend status)
  const [isIDValidationFlowComplete, setIsIDValidationFlowComplete] =
    useState(false);
  const [identityValidationActivities, setIdentityValidationActivities] =
    useState<Record<string, IdentityValidationActivity>>({});
  const [isPOAValidationFlowComplete, setIsPOAValidationFlowComplete] =
    useState(false);

  // Ref to prevent duplicate navigation triggers
  const isNavigatingToNextRequirementRef = useRef(false);

  // Track if address confirmation has been updated in the current tab session
  const [isAddressConfirmedUpdated, setIsAddressConfirmedUpdated] =
    useState(false);
  const [manualIdentityEntry, setManualIdentityEntry] = useState<{
    folderId: string;
    requirementId: string;
  } | null>(null);

  // Store the ID submit function so we can call it from "Siguiente" button
  const idSubmitFnRef = useRef<(() => Promise<boolean>) | null>(null);

  // Store the ProofOfAddress submit function so we can call it from "Siguiente" button
  const proofOfAddressSubmitFnRef = useRef<(() => Promise<boolean>) | null>(
    null,
  );

  // The general submit registration (forms/documents) triggered from
  // "Siguiente". A single slot keyed by the requirement it was registered
  // for: GeneralForm intentionally never unregisters on unmount (see its
  // onRegisterSubmit effect), so after navigating to a requirement that
  // doesn't register a submit the slot still names the previous form —
  // "Siguiente" must never run a submit registered for a different
  // requirement. `count` tracks overlapping instances for the SAME
  // requirement (AnimatePresence popLayout exit/re-enter, StrictMode
  // double-mount) so the outgoing instance's unregister doesn't clear the
  // incoming one; a different requirement id supersedes the slot outright.
  const generalSubmitRegistrationRef = useRef<{
    id: string | null;
    fn: () => Promise<boolean>;
    count: number;
  } | null>(null);
  // State twin of the slot's id so isCurrentRequirementSubmittable (and the
  // UI it drives) re-derives on registration changes.
  const [generalSubmitRequirementId, setGeneralSubmitRequirementId] = useState<
    string | null
  >(null);
  const [dataValidationAdvance, setDataValidationAdvance] = useState<{
    scope: string;
    canAdvance: boolean;
  } | null>(null);
  const activeRequirementIdRef = useRef<string | null>(null);
  const { isPending: isSubmitting, run: runSingleFlight } = useSingleFlight();

  // A verification sub-step can hide only its own requirement's navigation.
  const [navigationHiddenRequirementId, setNavigationHiddenRequirementId] =
    useState<string | null>(null);
  const [bdcCompanyDataEditRequirementId, setBdcCompanyDataEditRequirementId] =
    useState<string | null>(null);
  const [
    optimisticallyReviewedBdcRequirementId,
    setOptimisticallyReviewedBdcRequirementId,
  ] = useState<string | null>(null);

  // Pending navigation action for state-based navigation guard
  const [pendingNavAction, setPendingNavAction] = useState<
    (() => void | Promise<void>) | null
  >(null);
  const isConfirmingBlockedNavigationRef = useRef(false);
  const clearPendingNav = useCallback(() => setPendingNavAction(null), []);
  const runPendingNavAction = useCallback(() => {
    if (!pendingNavAction) return;

    const action = pendingNavAction;
    clearPendingNav();
    isConfirmingBlockedNavigationRef.current = true;
    try {
      // Every queued handler checks the bypass synchronously before its first
      // await. Reset it as soon as the handler is invoked so unrelated clicks
      // cannot bypass the verification guard for the lifetime of a request.
      const result = action();
      void Promise.resolve(result).catch((error) => {
        Sentry.captureException(error);
      });
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      isConfirmingBlockedNavigationRef.current = false;
    }
  }, [pendingNavAction, clearPendingNav]);

  // Get subfolders and sort them by priority
  const subfolders = useMemo(() => {
    const rawSubfolders = folder.subfolders || [];
    const sorted = [...rawSubfolders].sort((a, b) => {
      const orderA = subfolderOrder[a.slug] ?? 99;
      const orderB = subfolderOrder[b.slug] ?? 99;
      if (orderA !== orderB) return orderA - orderB;
      // Same group (e.g. several additional participants of the same role): keep them
      // in a stable, logical sequence by person_index instead of creation order.
      const idxA = a.metadata?.person_index ?? 0;
      const idxB = b.metadata?.person_index ?? 0;
      return idxA - idxB;
    });

    // Place each owner representative right after the owner it represents, so the
    // relationship is visible in the tab order.
    const isRep = (sf: Subfolder) => sf.slug.startsWith("owner-representative");
    const reps = sorted.filter(isRep);
    if (reps.length === 0) return sorted;

    const repsByOwner = new Map<string, Subfolder[]>();
    for (const rep of reps) {
      const ownerId = rep.metadata?.represents_subfolder_id;
      if (!ownerId) continue;
      repsByOwner.set(ownerId, [...(repsByOwner.get(ownerId) ?? []), rep]);
    }

    const result: Subfolder[] = [];
    for (const sf of sorted) {
      if (isRep(sf)) continue; // reps are inserted right after their owner
      result.push(sf);
      const owned = repsByOwner.get(sf.id);
      if (owned) result.push(...owned);
    }
    // Append orphan reps (missing owner / no pointer) at the end, preserving order.
    const placed = new Set(result);
    for (const rep of reps) {
      if (!placed.has(rep)) result.push(rep);
    }
    return result;
  }, [folder.subfolders]);
  // Active tab identity is the subfolder.id (not slug) so several subfolders of the same
  // role (one per participant) can be selected independently.
  const hasValidActiveTab = Boolean(
    activeTab && subfolders.some((subfolder) => subfolder.id === activeTab),
  );
  const currentTab = hasValidActiveTab ? activeTab! : subfolders[0]?.id || "";
  const currentSubfolder = subfolders.find((sf) => sf.id === currentTab);
  const currentSlug = currentSubfolder?.slug || "";
  const requirements = useMemo(
    () => currentSubfolder?.requirements ?? [],
    [currentSubfolder],
  );

  // Sync activeTab to Redux on mount y corrige slugs obsoletos al cambiar de expediente
  useEffect(() => {
    if (currentTab && activeTab !== currentTab) {
      dispatch(setActiveVaultTab(currentTab));
    }
  }, [activeTab, currentTab, dispatch]);

  // Get the tab configuration for the current tab (includes lockedFormSlug)
  const currentTabConfig = useMemo(
    () => getTabConfig(currentSlug),
    [currentSlug],
  );

  const bdcRequirement = useMemo(() => {
    return requirements.find((r) =>
      isBdcAuthorizationRequirement(r.slug, r.type),
    );
  }, [requirements]);
  const lockedFormSlugs = currentTabConfig?.lockedFormSlugs || null;
  const hasBdcAuthorization = Boolean(bdcRequirement);
  const supportsNativeBdcParticipant = isNativeBdcSupported(
    bdcRequirement,
    currentSubfolder,
  );
  // The parent shares this actor-scoped read with the native BDC component.
  // Navigation can resume a current Buró run without reopening modern reviews.
  const canReadBdcStatus = Boolean(
    jwtToken &&
      currentSubfolder &&
      supportsNativeBdcParticipant &&
      !isBdcActorMismatch(jwtToken, currentSubfolder.slug),
  );
  const bdcStatusQuery = useBdcStatus(bdcRequirement?.id, {
    enabled: canReadBdcStatus,
  });
  // A failed refetch retains the last successful read in this requirement's
  // actor-scoped cache. Keep its run accessible so native BDC can show retry;
  // an initial failed read has no data and cannot bypass the reviews.
  const verifiedBdcStatus = canReadBdcStatus ? bdcStatusQuery.data : undefined;
  const hasKnownCircleAuthorization =
    verifiedBdcStatus?.sic_source === "circulo_de_credito";
  const hasNativeBdcAuthorization =
    supportsNativeBdcParticipant && !hasKnownCircleAuthorization;
  const existingBdcRunRequirementId = hasVerifiedExistingBdcRun(
    bdcRequirement,
    verifiedBdcStatus,
  )
    ? bdcRequirement!.id
    : null;
  const knownBdcStatus = canReadBdcStatus ? bdcStatusQuery.data : undefined;
  const bdcReportReplacementRequirementId = isBdcReportReplacementPending(
    bdcRequirement,
    knownBdcStatus,
  )
    ? bdcRequirement!.id
    : null;
  const bdcAuthoritativeRequirementStatus =
    knownBdcStatus?.requirement_status ??
    (bdcRequirement && canReadBdcStatus && bdcStatusQuery.isPending
      ? Status.PendingUpload
      : undefined);
  const legacyCompletedBdcRequirementId =
    bdcRequirement &&
    bdcRequirement?.status === Status.Valid &&
    bdcRequirement?.is_native_bdc_run !== true &&
    (verifiedBdcStatus
      ? verifiedBdcStatus.status === "completed" &&
        verifiedBdcStatus.is_active === true &&
        ["buro_de_credito", "circulo_de_credito"].includes(
          verifiedBdcStatus.sic_source ?? "",
        ) &&
        verifiedBdcStatus.requirement_status === Status.Valid &&
        verifiedBdcStatus.is_native_bdc_run === false
      : bdcRequirement.is_native_bdc_run === false)
      ? bdcRequirement.id
      : null;
  const replacementRefreshRef = useRef<string | null>(null);
  useEffect(() => {
    const needsFolderRefresh = Boolean(
      bdcRequirement &&
        knownBdcStatus?.requirement_status &&
        knownBdcStatus.requirement_status !== bdcRequirement.status,
    );
    if (!bdcReportReplacementRequirementId && !needsFolderRefresh) {
      replacementRefreshRef.current = null;
      return;
    }
    const transition = `${folder.id}:${bdcRequirement?.id}:${bdcRequirement?.status}:${bdcAuthoritativeRequirementStatus}`;
    if (replacementRefreshRef.current === transition) return;
    replacementRefreshRef.current = transition;
    void invalidateVaultFolder();
  }, [
    folder.id,
    bdcRequirement,
    knownBdcStatus?.requirement_status,
    bdcReportReplacementRequirementId,
    bdcAuthoritativeRequirementStatus,
    invalidateVaultFolder,
  ]);

  // Find the ID requirement using the same BDC-aware identity classification
  // that controls sidebar grouping. Type-only custom Photo IDs are identity
  // documents only in a BDC flow; explicit official-ID slugs always qualify.
  const idRequirement = useMemo(() => {
    return requirements.find(
      (requirement) =>
        isIdentityRequirement(requirement) &&
        getRequirementGroup(
          requirement.slug,
          requirement.type,
          lockedFormSlugs,
          hasBdcAuthorization,
        ) === "identity",
    );
  }, [requirements, lockedFormSlugs, hasBdcAuthorization]);
  const identityValidationActivity = idRequirement
    ? (identityValidationActivities[idRequirement.id] ?? null)
    : null;

  // Find the proof of address requirement
  const proofOfAddressRequirement = useMemo(() => {
    return requirements.find(
      (requirement) =>
        isProofOfAddressRequirement(requirement) &&
        getRequirementGroup(
          requirement.slug,
          requirement.type,
          lockedFormSlugs,
          hasBdcAuthorization,
        ) === "identity",
    );
  }, [requirements, lockedFormSlugs, hasBdcAuthorization]);
  const isBdcAuthorizationTerminal =
    bdcRequirement?.status === Status.Valid ||
    bdcRequirement?.status === Status.PendingReview;
  const hasApprovedPersonForm = hasValidatedPersonForm(requirements);
  const persistedBdc = bdcRequirement?.verification_data?.bdc;
  const confirmedIDData = idRequirement?.verification_data?.confirmed_data;
  const effectiveBdcSicSource = getBdcSicSource(
    verifiedBdcStatus,
    bdcRequirement,
  );
  const bdcStatusForIdentityValidation =
    verifiedBdcStatus &&
    !Object.prototype.hasOwnProperty.call(verifiedBdcStatus, "sic_source")
      ? { ...verifiedBdcStatus, sic_source: effectiveBdcSicSource }
      : verifiedBdcStatus;
  const hasStrictBdcIdentityErrors = Boolean(
    confirmedIDData &&
      !hasValidIDData(confirmedIDData, {
        requireDialCode: false,
        requireStrictCurp: true,
        requireExteriorNumber: true,
        requireBirthDate: isBdcNaturalPersonSubfolder(currentSlug),
        strictBdcValidation: true,
      }),
  );
  const canValidateEditableBdcIdentity = Boolean(
    hasNativeBdcAuthorization &&
      !hasApprovedPersonForm &&
      effectiveBdcSicSource === "buro_de_credito" &&
      canCaptureBdcIdentity(bdcRequirement, bdcStatusForIdentityValidation),
  );
  const isAwaitingEditableBdcClassification = Boolean(
    canReadBdcStatus &&
      hasNativeBdcAuthorization &&
      !hasApprovedPersonForm &&
      bdcRequirement?.status === Status.PendingUpload &&
      bdcStatusQuery.isPending &&
      !verifiedBdcStatus &&
      hasStrictBdcIdentityErrors &&
      (effectiveBdcSicSource === "buro_de_credito" ||
        !Object.prototype.hasOwnProperty.call(
          persistedBdc ?? {},
          "sic_source",
        )) &&
      // While the first status read is unresolved, keep an invalid stored
      // identity away from consent. Once a response arrives, explicit null or
      // unknown sources no longer qualify for this strict Buró gate.
      !persistedBdc?.inputs_consumed_at &&
      !persistedBdc?.start_recovery_pending &&
      (!persistedBdc?.last_status ||
        persistedBdc.last_status === "capturing_inputs"),
  );

  const hasCompletedConfirmedIDData = useMemo(() => {
    const confirmedData = idRequirement?.verification_data?.confirmed_data;
    if (isAwaitingEditableBdcClassification) return false;
    if (!hasNativeBdcAuthorization) return Boolean(confirmedData);
    return (
      hasValidIDData(confirmedData, {
        requireDialCode: false,
        requireStrictCurp: true,
        requireExteriorNumber: true,
        requireBirthDate: isBdcNaturalPersonSubfolder(currentSlug),
        strictBdcValidation: canValidateEditableBdcIdentity,
      }) && isDataValidationSubmittedWorkflowStep(idRequirement?.workflow_step)
    );
  }, [
    hasNativeBdcAuthorization,
    idRequirement,
    currentSlug,
    canValidateEditableBdcIdentity,
    isAwaitingEditableBdcClassification,
  ]);

  const hasCompletedBdcDestinationReview = useMemo(() => {
    const confirmedData = idRequirement?.verification_data?.confirmed_data;
    return (
      !hasNativeBdcAuthorization ||
      (verifiedBdcStatus?.identity_recovery_eligible !== true &&
        hasReviewedNipDestination(confirmedData))
    );
  }, [
    hasNativeBdcAuthorization,
    idRequirement,
    verifiedBdcStatus?.identity_recovery_eligible,
  ]);

  // Approval freezes the personal form, not a missing ID snapshot. Keep an
  // active confirmation mounted if another session approves the form.
  const needsApprovedIdentityConfirmation = Boolean(
    hasApprovedPersonForm &&
      hasNativeBdcAuthorization &&
      !hasCompletedConfirmedIDData &&
      canCaptureBdcIdentity(bdcRequirement, verifiedBdcStatus) &&
      (verifiedBdcStatus?.identity_recovery_eligible !== true ||
        storeActiveRequirement?.slug === DATA_VALIDATION_SLUG),
  );

  // Persisted workflow state is authoritative over transient child state.
  const isIDCompleted = useMemo(() => {
    if (identityValidationActivity !== null) return false;

    if (!idRequirement) return true;
    if (
      idRequirement.workflow_step &&
      ["upload", "validating", "address_check", "rejected"].includes(
        idRequirement.workflow_step,
      )
    ) {
      return false;
    }
    return (
      isIdentityRequirementComplete(idRequirement) || isIDValidationFlowComplete
    );
  }, [idRequirement, identityValidationActivity, isIDValidationFlowComplete]);

  const isProofOfAddressCompleted = useMemo(() => {
    if (!proofOfAddressRequirement) return true;
    if (bdcRequirement) {
      return (
        (proofOfAddressRequirement.status === Status.Valid &&
          (proofOfAddressRequirement.workflow_step == null ||
            proofOfAddressRequirement.workflow_step === "completed" ||
            proofOfAddressRequirement.workflow_step === "pending_review")) ||
        (proofOfAddressRequirement.status === Status.PendingReview &&
          proofOfAddressRequirement.workflow_step === "completed" &&
          (proofOfAddressRequirement.verification_data
            ?.cross_validated_from_identity !== true ||
            isTrustedCrossValidatedProofOfAddress(
              proofOfAddressRequirement,
              idRequirement,
            )))
      );
    }
    return isRequirementVisuallyComplete(proofOfAddressRequirement);
  }, [bdcRequirement, idRequirement, proofOfAddressRequirement]);

  const isDataValidationCompleted = useMemo(() => {
    if (!idRequirement) return true;
    // An approved personal form and a valid BDC already establish completion.
    // Historical folders may not have the newer confirmed_data snapshot, and
    // their approved form must not be reopened just to navigate to signature.
    if (
      hasApprovedPersonForm &&
      (bdcRequirement?.status === Status.Valid || hasKnownCircleAuthorization)
    )
      return true;
    return hasCompletedConfirmedIDData;
  }, [
    idRequirement,
    hasApprovedPersonForm,
    hasKnownCircleAuthorization,
    bdcRequirement?.status,
    hasCompletedConfirmedIDData,
  ]);

  // Requirements reordered to match the sidebar's visual grouping:
  // identity group first (ID, proof of address, locked forms), then additional group.
  // All navigation (next/previous/step index) uses this order.
  const navigationRequirements = useMemo(() => {
    const sorted = sortRequirementsByGroup(
      requirements,
      currentTabConfig?.lockedFormSlugs || null,
    ).filter(
      (requirement) =>
        !isBdcCompanyDataRequirement(requirement.slug, requirement.type),
    );

    // Approved forms remain immutable; pre-Start confirmation only writes the
    // identity snapshot when server-side recovery cannot supply it.
    if (
      idRequirement &&
      (!hasApprovedPersonForm || needsApprovedIdentityConfirmation) &&
      (!existingBdcRunRequirementId || legacyCompletedBdcRequirementId)
    ) {
      const virtualReq: Requirement = {
        id: `virtual-data-validation-${idRequirement.id}`,
        name: "Validacion de Informacion",
        slug: DATA_VALIDATION_SLUG,
        type: Type.Form,
        status:
          hasCompletedConfirmedIDData &&
          (hasCompletedBdcDestinationReview || isBdcAuthorizationTerminal)
            ? Status.Valid
            : Status.PendingUpload,
        subtype: null,
        files: [],
        subfolder: idRequirement.subfolder,
      };

      if (proofOfAddressRequirement) {
        // Insert after POA in the sorted array
        const poaIndex = sorted.findIndex(
          (requirement) => requirement.id === proofOfAddressRequirement.id,
        );
        if (poaIndex !== -1) {
          sorted.splice(poaIndex + 1, 0, virtualReq);
        }
      } else {
        // No POA — insert after ID requirement
        const idIndex = sorted.findIndex(
          (requirement) => requirement.id === idRequirement.id,
        );
        if (idIndex !== -1) {
          sorted.splice(idIndex + 1, 0, virtualReq);
        }
      }
    }

    // Approved personal data cannot be rewritten by DataValidation. Contact
    // review has its own endpoint and keeps BDC's strict data prerequisites.
    if (
      hasApprovedPersonForm &&
      bdcRequirement &&
      hasNativeBdcAuthorization &&
      !needsApprovedIdentityConfirmation &&
      !isBdcAuthorizationTerminal &&
      !existingBdcRunRequirementId &&
      !hasCompletedBdcDestinationReview
    ) {
      const contactReview: Requirement = {
        id: `virtual-bdc-contact-review-${bdcRequirement.id}`,
        name: "Contacto para Buró de Crédito",
        slug: BDC_CONTACT_REVIEW_SLUG,
        type: Type.Form,
        status: Status.PendingUpload,
        subtype: null,
        files: [],
        subfolder: bdcRequirement.subfolder,
      };
      const identityIndex = sorted.findIndex(
        (requirement) =>
          requirement.id ===
          (proofOfAddressRequirement?.id ?? idRequirement?.id),
      );
      const bdcIndex = sorted.findIndex((requirement) =>
        isBdcAuthorizationRequirement(requirement.slug, requirement.type),
      );
      sorted.splice(
        identityIndex >= 0 ? identityIndex + 1 : bdcIndex,
        0,
        contactReview,
      );
    }

    // Persona moral reviews a virtual snapshot stored on bdc-authorization.
    // This deliberately does not depend on a separate Requirement row, so
    // folders created before the native BDC release need no data backfill.
    if (
      bdcRequirement &&
      hasNativeBdcAuthorization &&
      currentSlug.endsWith("-pm") &&
      !existingBdcRunRequirementId
    ) {
      const companyData = bdcRequirement.verification_data?.bdc?.company_data;
      const virtualCompanyRequirement: Requirement = {
        ...createBdcCompanyDataRequirement(
          bdcRequirement,
          bdcRequirement.status === Status.Valid ||
            bdcRequirement.status === Status.PendingReview ||
            companyData?.reviewed === true ||
            optimisticallyReviewedBdcRequirementId === bdcRequirement.id
            ? Status.Valid
            : Status.PendingUpload,
        ),
        verification_data: { bdc: { company_data: companyData } },
      };
      const bdcIndex = sorted.findIndex((requirement) =>
        isBdcAuthorizationRequirement(requirement.slug, requirement.type),
      );
      if (bdcIndex >= 0) {
        sorted.splice(bdcIndex, 0, virtualCompanyRequirement);
      }
    }

    return sorted;
  }, [
    requirements,
    currentTabConfig,
    idRequirement,
    proofOfAddressRequirement,
    hasCompletedConfirmedIDData,
    hasCompletedBdcDestinationReview,
    isBdcAuthorizationTerminal,
    hasApprovedPersonForm,
    needsApprovedIdentityConfirmation,
    hasNativeBdcAuthorization,
    bdcRequirement,
    legacyCompletedBdcRequirementId,
    currentSlug,
    optimisticallyReviewedBdcRequirementId,
    existingBdcRunRequirementId,
  ]);

  // Get subfolder slug map for rendering
  const subfolderSlugMap = useMemo(
    () => getSubfoldersMapByIdToSlug(folder),
    [folder],
  );

  // Current requirement — if Redux has one, sync it; otherwise smart-select
  // the first requirement that still needs attention (not Valid).
  // This avoids always landing on step 1 when revisiting a partially-completed vault.
  const activeRequirement = useMemo(() => {
    // No stored requirement — pick the first that still needs user action.
    // Pick the first persisted incomplete step. Identity needs its workflow
    // metadata to disambiguate terminal pending_review from upload binding.
    const dataValidationRequirement = navigationRequirements.find(
      (r) =>
        r.slug === DATA_VALIDATION_SLUG || r.slug === BDC_CONTACT_REVIEW_SLUG,
    );
    const isRequirementEntryBlocked = (requirement: Requirement) =>
      isRequirementLockedByPrerequisites(requirement, {
        requirements: navigationRequirements,
        lockedFormSlugs: currentTabConfig?.lockedFormSlugs || null,
        bdcPrerequisiteSlugs: currentTabConfig?.bdcPrerequisiteSlugs || null,
        isIDCompleted,
        isProofOfAddressCompleted,
        isDataValidationCompleted,
        isBdcDestinationReviewed: hasCompletedBdcDestinationReview,
        existingBdcRunRequirementId,
        legacyCompletedBdcRequirementId,
        bdcReportReplacementRequirementId,
        bdcAuthoritativeRequirementStatus,
        isAddressConfirmedUpdated,
      });
    const firstIncomplete = navigationRequirements.find((r) => {
      if (isProofOfAddressProcessing(r)) return true;
      if (isProofOfAddressRequirement(r) && !isProofOfAddressCompleted) {
        return true;
      }
      if (isIdentityRequirement(r) && !isIdentityRequirementComplete(r)) {
        return true;
      }
      if (r.status !== Status.Valid && r.status !== Status.PendingReview)
        return true;
      if (
        r.workflow_step === "address_check" &&
        !r.verification_data?.confirmed_data
      ) {
        return true;
      }
      return false;
    });
    // Recover blocked BDC entry without changing the fallback for other flows.
    const getFallbackRequirement = () => {
      if (!bdcRequirement || (!idRequirement && !hasApprovedPersonForm)) {
        return firstIncomplete || navigationRequirements[0] || null;
      }
      if (firstIncomplete && !isRequirementEntryBlocked(firstIncomplete)) {
        return firstIncomplete;
      }
      if (
        firstIncomplete &&
        dataValidationRequirement &&
        !isRequirementEntryBlocked(dataValidationRequirement)
      ) {
        return dataValidationRequirement;
      }
      return (
        navigationRequirements.find(
          (requirement) => !isRequirementEntryBlocked(requirement),
        ) || null
      );
    };

    if (storeActiveRequirement) {
      const storedRequirement = navigationRequirements.find(
        (r) => r.id === storeActiveRequirement.id,
      );
      if (
        shouldKeepProofOfAddressActive(
          storedRequirement,
          proofOfAddressRequirement,
        )
      ) {
        return proofOfAddressRequirement;
      }
      const isStoredRequirementBlocked = Boolean(
        storedRequirement && isRequirementEntryBlocked(storedRequirement),
      );

      if (storedRequirement && !isStoredRequirementBlocked) {
        return storedRequirement;
      }

      if (
        storedRequirement &&
        isStoredRequirementBlocked &&
        isBdcAuthorizationRequirement(
          storedRequirement.slug,
          storedRequirement.type,
        ) &&
        firstIncomplete?.id === storedRequirement.id &&
        dataValidationRequirement &&
        !isRequirementEntryBlocked(dataValidationRequirement)
      ) {
        return dataValidationRequirement;
      }

      return getFallbackRequirement();
    }
    // No stored requirement — pick the first that still needs user action.
    // Both Valid and PendingReview are "done" (user can't act on PendingReview),
    // UNLESS the requirement has workflow_step="address_check" without confirmed_data
    // (meaning the user hasn't completed the address confirmation flow yet).
    if (
      firstIncomplete &&
      idRequirement !== undefined &&
      isBdcAuthorizationRequirement(
        firstIncomplete.slug,
        firstIncomplete.type,
      ) &&
      isRequirementEntryBlocked(firstIncomplete) &&
      dataValidationRequirement &&
      !isRequirementEntryBlocked(dataValidationRequirement)
    ) {
      return dataValidationRequirement;
    }

    return getFallbackRequirement();
  }, [
    storeActiveRequirement,
    navigationRequirements,
    hasApprovedPersonForm,
    bdcRequirement,
    proofOfAddressRequirement,
    idRequirement,
    isIDCompleted,
    isProofOfAddressCompleted,
    isDataValidationCompleted,
    hasCompletedBdcDestinationReview,
    existingBdcRunRequirementId,
    legacyCompletedBdcRequirementId,
    bdcReportReplacementRequirementId,
    bdcAuthoritativeRequirementStatus,
    isAddressConfirmedUpdated,
    currentTabConfig?.lockedFormSlugs,
    currentTabConfig?.bdcPrerequisiteSlugs,
  ]);

  // Kept in a ref so handleRegisterGeneralSubmit (a stable callback invoked
  // from child effects) can stamp registrations with the requirement they
  // were registered for. Same render-time ref-sync pattern as GeneralForm.
  activeRequirementIdRef.current = activeRequirement?.id ?? null;

  const isNavigationHidden = Boolean(
    navigationHiddenRequirementId &&
      navigationHiddenRequirementId === activeRequirement?.id,
  );
  // Toast actions can outlive the render that created them.
  const navigationHiddenRef = useRef(isNavigationHidden);
  navigationHiddenRef.current = isNavigationHidden;
  const { blocker } = useVerificationBlocker(isNavigationHidden);

  useEffect(() => {
    setNavigationHiddenRequirementId((previous) =>
      previous === activeRequirement?.id ? previous : null,
    );
  }, [activeRequirement?.id]);

  useEffect(() => {
    if (
      manualIdentityEntry &&
      (manualIdentityEntry.folderId !== folder.id ||
        manualIdentityEntry.requirementId !== activeRequirement?.id)
    ) {
      setManualIdentityEntry(null);
    }
  }, [activeRequirement?.id, folder.id, manualIdentityEntry]);

  const markManualIdentityEntry = useCallback(
    (requirement: Requirement) => {
      setManualIdentityEntry(
        isIdentityRequirement(requirement)
          ? { folderId: folder.id, requirementId: requirement.id }
          : null,
      );
    },
    [folder.id],
  );

  // Initialize activeRequirement in store on mount or when it changes.
  // Also sync the previousRequirementIndex so next navigation direction is correct.
  useEffect(() => {
    // Preserve the requested step until the first actor read decides whether
    // modern reviews belong to this run. Background refetches keep their data.
    if (bdcRequirement && canReadBdcStatus && bdcStatusQuery.isPending) return;
    if (
      activeRequirement &&
      storeActiveRequirement?.id !== activeRequirement.id
    ) {
      dispatch(setActiveRequirement(activeRequirement));
      const idx = navigationRequirements.findIndex(
        (r) => r.id === activeRequirement.id,
      );
      if (idx >= 0) previousRequirementIndex.current = idx;
    }
  }, [
    activeRequirement,
    storeActiveRequirement,
    dispatch,
    navigationRequirements,
    bdcRequirement,
    canReadBdcStatus,
    bdcStatusQuery.isPending,
  ]);

  // Current step index (1-based, in navigation order)
  const currentStepIndex = useMemo(() => {
    if (!activeRequirement) return 1;
    const index = navigationRequirements.findIndex(
      (r) => r.id === activeRequirement.id,
    );
    return index >= 0 ? index + 1 : 1;
  }, [activeRequirement, navigationRequirements]);

  const isRequirementLocked = useCallback(
    (requirement: Requirement): boolean =>
      isRequirementLockedByPrerequisites(requirement, {
        requirements: navigationRequirements,
        lockedFormSlugs: currentTabConfig?.lockedFormSlugs || null,
        bdcPrerequisiteSlugs: currentTabConfig?.bdcPrerequisiteSlugs || null,
        isIDCompleted,
        isProofOfAddressCompleted,
        isDataValidationCompleted,
        isBdcDestinationReviewed: hasCompletedBdcDestinationReview,
        existingBdcRunRequirementId,
        legacyCompletedBdcRequirementId,
        bdcReportReplacementRequirementId,
        bdcAuthoritativeRequirementStatus,
        isAddressConfirmedUpdated,
      }),
    [
      navigationRequirements,
      currentTabConfig?.lockedFormSlugs,
      currentTabConfig?.bdcPrerequisiteSlugs,
      isIDCompleted,
      isProofOfAddressCompleted,
      isDataValidationCompleted,
      hasCompletedBdcDestinationReview,
      existingBdcRunRequirementId,
      legacyCompletedBdcRequirementId,
      bdcReportReplacementRequirementId,
      bdcAuthoritativeRequirementStatus,
      isAddressConfirmedUpdated,
    ],
  );

  // Reset validation flow state when changing tabs/subfolders
  useEffect(() => {
    setIsIDValidationFlowComplete(false);
    setIsPOAValidationFlowComplete(false);
    setIsAddressConfirmedUpdated(false);
    setOptimisticallyReviewedBdcRequirementId(null);
    isNavigatingToNextRequirementRef.current = false;
  }, [currentTab]);

  // Auto-dismiss section banner after 4 seconds
  useEffect(() => {
    if (!sectionBanner) return;
    const timer = setTimeout(() => setSectionBanner(null), 4000);
    return () => clearTimeout(timer);
  }, [sectionBanner]);

  // Helper to find requirement by ID (searches navigationRequirements to include virtual steps)
  const findRequirementById = useCallback(
    (id: string): Requirement | undefined => {
      return navigationRequirements.find((r) => r.id === id);
    },
    [navigationRequirements],
  );

  // Tab change handler
  const handleTabChange = useCallback(
    (tabId: string, skipHaptic?: boolean) => {
      if (isNavigationHidden && !isConfirmingBlockedNavigationRef.current) {
        setPendingNavAction(() => () => handleTabChange(tabId, skipHaptic));
        return;
      }
      if (!skipHaptic) hapticTrigger("heavy");
      // Show section banner for cross-tab navigation (not initial mount)
      if (activeTab && tabId !== activeTab) {
        const targetSubfolder = subfolders.find((sf) => sf.id === tabId);
        if (targetSubfolder) {
          const targetReqs = targetSubfolder.requirements || [];
          setSectionBanner({
            name:
              subfoldersMapping[targetSubfolder.slug] || targetSubfolder.name,
            description: subfolderDescriptions[targetSubfolder.slug] || "",
            stepCount: targetReqs.length,
          });
        }
      }

      dispatch(setActiveVaultTab(tabId));
      setManualIdentityEntry(null);
      // Reset direction to forward when changing tabs
      setNavigationDirection(1);
      previousRequirementIndex.current = 0;
      // Clear active requirement — the activeRequirement memo will smart-select
      // the first incomplete requirement for the new tab
      dispatch(clearActiveRequirement());

      // Scroll content to top
      contentScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    },
    [dispatch, activeTab, subfolders, hapticTrigger, isNavigationHidden],
  );

  // Requirement selection handler
  const handleSelectRequirement = useCallback(
    (requirementId: string) => {
      if (isNavigationHidden && !isConfirmingBlockedNavigationRef.current) {
        setPendingNavAction(() => () => handleSelectRequirement(requirementId));
        return;
      }
      const requirement = findRequirementById(requirementId);
      if (requirement) {
        if (isRequirementLocked(requirement)) {
          hapticTrigger("error");
          return;
        }
        hapticTrigger("medium");
        const newIndex = navigationRequirements.findIndex(
          (r) => r.id === requirementId,
        );
        setNavigationDirection(
          newIndex >= previousRequirementIndex.current ? 1 : -1,
        );
        previousRequirementIndex.current = newIndex;
        markManualIdentityEntry(requirement);
        dispatch(setActiveRequirement(requirement));
      }
      setIsStepSheetOpen(false);
    },
    [
      dispatch,
      findRequirementById,
      isRequirementLocked,
      navigationRequirements,
      hapticTrigger,
      isNavigationHidden,
      markManualIdentityEntry,
    ],
  );

  const handleNavigateToRequirement = useCallback(
    (
      requirement: Requirement,
      subfolderId: string,
      options?: { forceBdcCompanyDataEdit?: boolean },
    ) => {
      if (
        navigationHiddenRef.current &&
        !isConfirmingBlockedNavigationRef.current
      ) {
        setPendingNavAction(
          () => () =>
            handleNavigateToRequirement(requirement, subfolderId, options),
        );
        return;
      }
      const shouldForceBdcCompanyDataEdit =
        options?.forceBdcCompanyDataEdit === true &&
        isBdcCompanyDataRequirement(requirement.slug, requirement.type);
      const navigationRequirement = shouldForceBdcCompanyDataEdit
        ? (navigationRequirements.find((candidate) =>
            isBdcCompanyDataRequirement(candidate.slug, candidate.type),
          ) ?? requirement)
        : requirement;
      if (isRequirementLocked(navigationRequirement)) {
        hapticTrigger("error");
        return;
      }
      setBdcCompanyDataEditRequirementId(
        shouldForceBdcCompanyDataEdit ? navigationRequirement.id : null,
      );
      dispatch(setActiveVaultTab(subfolderId));

      const newIndex = navigationRequirements.findIndex(
        (candidate) => candidate.id === navigationRequirement.id,
      );
      if (newIndex >= 0) {
        setNavigationDirection(
          newIndex >= previousRequirementIndex.current ? 1 : -1,
        );
        previousRequirementIndex.current = newIndex;
      }
      dispatch(setActiveRequirement(navigationRequirement));
      markManualIdentityEntry(navigationRequirement);
      setIsStepSheetOpen(false);
    },
    [
      dispatch,
      navigationRequirements,
      isRequirementLocked,
      hapticTrigger,
      markManualIdentityEntry,
    ],
  );

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    if (isNavigationHidden && !isConfirmingBlockedNavigationRef.current) {
      setPendingNavAction(() => () => handlePrevious());
      return;
    }
    hapticTrigger("medium");
    const currentIndex = navigationRequirements.findIndex(
      (r) => r.id === activeRequirement?.id,
    );
    if (currentIndex > 0) {
      setNavigationDirection(-1);
      previousRequirementIndex.current = currentIndex - 1;
      markManualIdentityEntry(navigationRequirements[currentIndex - 1]);
      dispatch(setActiveRequirement(navigationRequirements[currentIndex - 1]));
    }
  }, [
    dispatch,
    navigationRequirements,
    activeRequirement,
    hapticTrigger,
    isNavigationHidden,
    markManualIdentityEntry,
  ]);

  // Check if the current requirement is an ID requirement
  const isCurrentRequirementID = useMemo(() => {
    if (!activeRequirement) return false;
    return activeRequirement.id === idRequirement?.id;
  }, [activeRequirement, idRequirement]);

  // Check if the current requirement is a Proof of Address requirement
  const isCurrentRequirementProofOfAddress = useMemo(() => {
    if (!activeRequirement) return false;
    return isProofOfAddressRequirement(activeRequirement);
  }, [activeRequirement]);

  const isCurrentRequirementBdcAuthorization = useMemo(() => {
    if (!activeRequirement) return false;
    return isBdcAuthorizationRequirement(
      activeRequirement.slug,
      activeRequirement.type,
    );
  }, [activeRequirement]);

  // BDC owns its in-progress controls. Once completed, restore the existing
  // footer so desktop and mobile can advance just like the sidebar.
  const hideGlobalStepNavigation =
    isNavigationHidden ||
    (isCurrentRequirementBdcAuthorization &&
      activeRequirement?.status !== Status.Valid);

  const canSubmitGeneralRequirement = useCallback(
    (requirement: Requirement): boolean =>
      validToUpload.includes(requirement.status) ||
      (isBdcCompanyDataRequirement(requirement.slug, requirement.type) &&
        !isBdcAuthorizationTerminal),
    [isBdcAuthorizationTerminal],
  );

  const currentSubmitMode =
    activeRequirement &&
    isBdcCompanyDataRequirement(activeRequirement.slug, activeRequirement.type)
      ? "continue"
      : "review";

  const nextRequirement = useMemo(() => {
    if (!activeRequirement) return null;
    const currentIndex = navigationRequirements.findIndex(
      (r) => r.id === activeRequirement.id,
    );
    if (currentIndex < 0 || currentIndex >= navigationRequirements.length - 1) {
      return null;
    }
    return navigationRequirements[currentIndex + 1] ?? null;
  }, [activeRequirement, navigationRequirements]);

  const canNavigateToNextRequirement = useMemo(() => {
    if (!nextRequirement) return false;
    if (
      isCurrentRequirementID ||
      (isCurrentRequirementProofOfAddress &&
        !isRequirementVisuallyComplete(activeRequirement))
    )
      return true;
    return !isRequirementLocked(nextRequirement);
  }, [
    nextRequirement,
    activeRequirement,
    isCurrentRequirementID,
    isCurrentRequirementProofOfAddress,
    isRequirementLocked,
  ]);

  const dataValidationScope = `${folder.id}:${currentTab}:${activeRequirement?.id}:${decodeVaultJwtActor(jwtToken)}`;
  const canAdvanceDataValidation =
    dataValidationAdvance?.scope === dataValidationScope &&
    dataValidationAdvance.canAdvance;
  const isDataValidationForwardBlocked = Boolean(
    bdcRequirement &&
      activeRequirement?.slug === DATA_VALIDATION_SLUG &&
      !canAdvanceDataValidation,
  );
  const handleDataValidationCanAdvanceChange = useCallback(
    (canAdvance: boolean) => {
      setDataValidationAdvance((previous) => {
        if (!canAdvance && previous?.scope !== dataValidationScope)
          return previous;
        return { scope: dataValidationScope, canAdvance };
      });
    },
    [dataValidationScope],
  );

  // Next tab info for cross-tab navigation (null if on last tab)
  const nextTabInfo = useMemo(() => {
    const currentTabIndex = subfolders.findIndex((sf) => sf.id === currentTab);
    if (currentTabIndex < 0 || currentTabIndex >= subfolders.length - 1)
      return null;
    const nextSf = subfolders[currentTabIndex + 1];
    return {
      id: nextSf.id,
      displayName: subfoldersMapping[nextSf.slug] || nextSf.name,
    };
  }, [subfolders, currentTab]);

  // Navigate to the next tab (cross-tab navigation)
  const goToNextTab = useCallback(() => {
    hapticTrigger("nudge");
    if (nextTabInfo) {
      handleTabChange(nextTabInfo.id, true);
    }
  }, [nextTabInfo, handleTabChange, hapticTrigger]);

  const handleNextTab = useCallback(async () => {
    await runSingleFlight(() => goToNextTab());
  }, [goToNextTab, runSingleFlight]);

  // Whether the current requirement is submittable via the "Siguiente" button
  const isCurrentRequirementSubmittable = useMemo(() => {
    if (!activeRequirement) return false;
    // Only a submit registered for THIS requirement counts; a leftover
    // registration from a previously viewed requirement must not make the
    // current one look submittable.
    if (generalSubmitRequirementId === null) return false;
    if (generalSubmitRequirementId !== activeRequirement.id) return false;
    return canSubmitGeneralRequirement(activeRequirement);
  }, [
    activeRequirement,
    canSubmitGeneralRequirement,
    generalSubmitRequirementId,
  ]);

  const handleNext = useCallback(async () => {
    await runSingleFlight(async () => {
      if (isDataValidationForwardBlocked) return;
      if (isNavigationHidden && !isConfirmingBlockedNavigationRef.current) {
        setPendingNavAction(() => () => handleNext());
        return;
      }
      if (isProofOfAddressProcessing(activeRequirement)) {
        hapticTrigger("error");
        return;
      }
      // Haptic is triggered contextually below (light for next req, nudge for next tab via handleNextTab)
      // If we're on the ID requirement and it needs validation, trigger validation first
      if (isCurrentRequirementID && idSubmitFnRef.current) {
        const idAlreadyDone = isIDCompleted;

        if (!idAlreadyDone) {
          // Trigger the ID validation flow
          const started = await idSubmitFnRef.current();
          // If validation started, don't navigate yet - the flow will handle completion
          if (started) {
            return;
          }
          // If validation didn't start (missing files), don't navigate
          return;
        }
      }

      // If we're on the Proof of Address requirement and it needs validation, trigger validation first
      if (
        isCurrentRequirementProofOfAddress &&
        proofOfAddressSubmitFnRef.current
      ) {
        const proofOfAddressAlreadyDone =
          isRequirementVisuallyComplete(activeRequirement);

        if (!proofOfAddressAlreadyDone) {
          // Trigger the Proof of Address validation flow
          const started = await proofOfAddressSubmitFnRef.current();
          if (!started) return;
          // The persisted async state keeps this requirement active. Polling
          // will refresh it and the completion effect advances only after a
          // terminal verdict, matching the former synchronous experience.
          return;
        }
      }

      // General submit handling (for GeneralForm, GeneralDocument, ProofOfIncome, etc.)
      // Read the registration slot at call time — it is the single source
      // of truth, so a registration that changed between render and click
      // can never run a submit for a form that is no longer on screen.
      const registration = generalSubmitRegistrationRef.current;
      const currentSubmitFn =
        registration && registration.id === activeRequirement?.id
          ? registration.fn
          : null;
      const canSubmitCurrentRequirement =
        Boolean(currentSubmitFn) &&
        Boolean(
          activeRequirement && canSubmitGeneralRequirement(activeRequirement),
        );
      if (isCurrentRequirementSubmittable && !canSubmitCurrentRequirement) {
        // The form unregistered between render and click. Fall through to
        // normal navigation (the draft autosave keeps the user's input)
        // instead of dead-ending the tap with a silent no-op.
        Sentry.addBreadcrumb({
          category: "vault.navigation",
          message: "Skipped stale general submit registration",
          level: "warning",
          data: {
            activeRequirementId: activeRequirement?.id,
            registeredRequirementId: registration?.id ?? null,
          },
        });
      }
      if (canSubmitCurrentRequirement && currentSubmitFn) {
        try {
          const success = await currentSubmitFn();
          if (!success) {
            // Submission failed (validation error, API error, etc.) - stay on current
            return;
          }
          if (
            activeRequirement &&
            isBdcCompanyDataRequirement(
              activeRequirement.slug,
              activeRequirement.type,
            ) &&
            bdcRequirement
          ) {
            const bdcRequirementIndex = navigationRequirements.findIndex(
              (requirement) => requirement.id === bdcRequirement.id,
            );
            setOptimisticallyReviewedBdcRequirementId(bdcRequirement.id);
            isNavigatingToNextRequirementRef.current = true;
            hapticTrigger("medium");
            setNavigationDirection(1);
            if (bdcRequirementIndex >= 0) {
              previousRequirementIndex.current = bdcRequirementIndex;
            }
            dispatch(setActiveRequirement(bdcRequirement));
            setTimeout(() => {
              isNavigatingToNextRequirementRef.current = false;
            }, 100);
            return;
          }
          // Submission succeeded - continue to navigate below
        } catch {
          // Unexpected error - stay on current
          return;
        }
      }

      // Normal navigation to next requirement (using grouped order)
      const currentIndex = navigationRequirements.findIndex(
        (r) => r.id === activeRequirement?.id,
      );
      if (currentIndex < navigationRequirements.length - 1) {
        const nextRequirement = navigationRequirements[currentIndex + 1];
        if (!nextRequirement || isRequirementLocked(nextRequirement)) {
          hapticTrigger("error");
          return;
        }
        hapticTrigger("medium");
        setNavigationDirection(1);
        previousRequirementIndex.current = currentIndex + 1;
        dispatch(setActiveRequirement(nextRequirement));
      } else if (nextTabInfo) {
        // Last requirement of the tab — navigate to the next tab
        goToNextTab();
      }
    });
  }, [
    activeRequirement,
    isDataValidationForwardBlocked,
    bdcRequirement,
    dispatch,
    goToNextTab,
    hapticTrigger,
    isCurrentRequirementID,
    isCurrentRequirementProofOfAddress,
    isCurrentRequirementSubmittable,
    isIDCompleted,
    isNavigationHidden,
    isRequirementLocked,
    canSubmitGeneralRequirement,
    navigationRequirements,
    nextTabInfo,
    runSingleFlight,
  ]);

  const isForwardDisabled =
    isDataValidationForwardBlocked ||
    isProofOfAddressProcessing(activeRequirement) ||
    (isCurrentRequirementID &&
      isIdentityValidationActivityProcessing(identityValidationActivity));

  // Whether we're on the last requirement of the current tab
  const isLastRequirement = currentStepIndex >= navigationRequirements.length;

  // Callback when ID validation flow completes
  const handleIDValidationComplete = useCallback((isComplete: boolean) => {
    setIsIDValidationFlowComplete(isComplete);
  }, []);

  const handleIDValidationActivityChange = useCallback(
    ({ requirementId, activity }: IdentityValidationActivityUpdate) => {
      setIdentityValidationActivities((current) => {
        const next = { ...current };
        if (activity === null) delete next[requirementId];
        else next[requirementId] = activity;
        return next;
      });
    },
    [],
  );

  // A local failure bridges a failed/stale refresh. Once a later folder result
  // proves that this requirement is no longer processing, persisted state is
  // authoritative again and should drive the sidebar locks on its own.
  useEffect(() => {
    if (
      !idRequirement ||
      identityValidationActivity !== "failed" ||
      !hasSettledPersistedIdentityValidation(idRequirement)
    ) {
      return;
    }

    handleIDValidationActivityChange({
      requirementId: idRequirement.id,
      activity: null,
    });
  }, [
    handleIDValidationActivityChange,
    idRequirement,
    identityValidationActivity,
  ]);

  // Auto-navigate to next requirement when ID validation flow completes (non-INE docs)
  // INE "Sí" case (with POA) is handled directly via onNavigateToDataValidation callback
  // INE "No" case is handled via shouldNavigateToProofOfAddress in ID.tsx
  // For ID-only subfolders (no POA), address_check is skipped so we auto-advance here
  useEffect(() => {
    if (
      !isIDValidationFlowComplete ||
      !isCurrentRequirementID ||
      identityValidationActivity !== null ||
      !isIDCompleted ||
      manualIdentityEntry?.requirementId === activeRequirement?.id
    ) {
      return;
    }
    // Skip if address was confirmed AND POA exists — navigation handled by onNavigateToDataValidation callback
    // For ID-only subfolders (no POA), we always auto-advance since address_check is skipped
    if (isAddressConfirmedUpdated && proofOfAddressRequirement) return;
    // Prevent duplicate navigation
    if (isNavigatingToNextRequirementRef.current) return;

    const currentIndex = navigationRequirements.findIndex(
      (r) => r.id === activeRequirement?.id,
    );
    if (currentIndex >= 0 && currentIndex < navigationRequirements.length - 1) {
      isNavigatingToNextRequirementRef.current = true;
      let didNavigate = false;
      const timer = setTimeout(() => {
        const nextRequirement = navigationRequirements[currentIndex + 1];
        if (isRequirementLocked(nextRequirement)) {
          isNavigatingToNextRequirementRef.current = false;
          return;
        }

        didNavigate = true;
        setNavigationDirection(1);
        previousRequirementIndex.current = currentIndex + 1;
        dispatch(
          setActiveRequirement(navigationRequirements[currentIndex + 1]),
        );
        setTimeout(() => {
          isNavigatingToNextRequirementRef.current = false;
        }, 100);
      }, 600);
      return () => {
        clearTimeout(timer);
        if (!didNavigate) {
          isNavigatingToNextRequirementRef.current = false;
        }
      };
    }
  }, [
    isIDValidationFlowComplete,
    isCurrentRequirementID,
    navigationRequirements,
    activeRequirement,
    dispatch,
    isAddressConfirmedUpdated,
    proofOfAddressRequirement,
    identityValidationActivity,
    isIDCompleted,
    isRequirementLocked,
    manualIdentityEntry,
  ]);

  // Callback when POA validation flow completes
  const handlePOAValidationComplete = useCallback((isComplete: boolean) => {
    setIsPOAValidationFlowComplete(isComplete);
  }, []);

  // Auto-navigate to DataValidation when POA validation flow completes
  useEffect(() => {
    if (!isPOAValidationFlowComplete || !isCurrentRequirementProofOfAddress)
      return;
    if (isNavigatingToNextRequirementRef.current) return;

    const dataValidationReq = navigationRequirements.find(
      (r) => r.slug === DATA_VALIDATION_SLUG,
    );
    if (dataValidationReq) {
      isNavigatingToNextRequirementRef.current = true;
      const timer = setTimeout(() => {
        const newIndex = navigationRequirements.findIndex(
          (r) => r.id === dataValidationReq.id,
        );
        setNavigationDirection(1);
        previousRequirementIndex.current = newIndex;
        dispatch(setActiveRequirement(dataValidationReq));
        setTimeout(() => {
          isNavigatingToNextRequirementRef.current = false;
        }, 100);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [
    isPOAValidationFlowComplete,
    isCurrentRequirementProofOfAddress,
    navigationRequirements,
    dispatch,
  ]);

  // Callback when address confirmation result is received
  const handleAddressConfirmationResult = useCallback((isUpdated: boolean) => {
    setManualIdentityEntry(null);
    setIsAddressConfirmedUpdated(isUpdated);
  }, []);

  // Callback to register the ID submit function
  const handleRegisterIDSubmit = useCallback(
    (submitFn: () => Promise<boolean>) => {
      idSubmitFnRef.current = submitFn;
    },
    [],
  );

  // Callback to register the Proof of Address submit function
  const handleRegisterProofOfAddressSubmit = useCallback(
    (submitFn: () => Promise<boolean>) => {
      proofOfAddressSubmitFnRef.current = submitFn;
    },
    [],
  );

  // Callback to register the general submit function (forms/documents).
  // `requirementId` is bound registrant-side (see the per-requirement
  // registrar in documentationMapOptions): AnimatePresence freezes exiting
  // elements (and thus the registrar closure they carry) while the
  // Redux-connected components inside them keep re-rendering with the NEW
  // requirement and can mount rogue instances. Those rogues register with
  // the OLD requirement's id and are ignored here — regardless of the order
  // React runs the entering/exiting subtrees' effects in.
  const handleRegisterGeneralSubmit = useCallback(
    (
      submitFn: (() => Promise<boolean>) | null,
      requirementId: string | null,
    ) => {
      const registration = generalSubmitRegistrationRef.current;
      if (submitFn !== null) {
        if (requirementId !== activeRequirementIdRef.current) {
          Sentry.addBreadcrumb({
            category: "vault.navigation",
            message:
              "Ignored submit registration from exiting requirement subtree",
            level: "info",
            data: {
              registrantRequirementId: requirementId,
              activeRequirementId: activeRequirementIdRef.current,
            },
          });
          return;
        }
        // Same requirement → stack on the count (overlapping instances);
        // different requirement → supersede the slot outright. The
        // superseded instances' late unregisters carry the old id and no
        // longer match, so they can't drift the count.
        generalSubmitRegistrationRef.current = {
          id: requirementId,
          fn: submitFn,
          count:
            registration?.id === requirementId ? registration.count + 1 : 1,
        };
        setGeneralSubmitRequirementId(requirementId);
      } else {
        // Only a registrant for the currently registered requirement may
        // unregister; stale cleanups from exited subtrees are ignored.
        if (!registration || requirementId !== registration.id) return;
        if (registration.count > 1) {
          registration.count -= 1;
          return;
        }
        generalSubmitRegistrationRef.current = null;
        setGeneralSubmitRequirementId(null);
      }
    },
    [],
  );

  // Registration callback bound to the requirement an element renders for.
  // Memoized per requirement id so children's registration effects re-run
  // exactly on requirement changes, and so exiting AnimatePresence elements
  // keep the registrar of the requirement they were created for.
  const generalSubmitRegistrar = useMemo(() => {
    const id = activeRequirement?.id ?? null;
    return (submitFn: (() => Promise<boolean>) | null) =>
      handleRegisterGeneralSubmit(submitFn, id);
  }, [activeRequirement?.id, handleRegisterGeneralSubmit]);

  // Exiting animated requirements keep their own callback. Their cleanup must
  // not reveal navigation underneath the next requirement's address question.
  const handleSetNavigationHidden = useMemo(() => {
    const requirementId = activeRequirement?.id ?? null;
    return (hidden: boolean) => {
      if (activeRequirementIdRef.current === requirementId) {
        setNavigationHiddenRequirementId(hidden ? requirementId : null);
      }
    };
  }, [activeRequirement?.id]);

  const handleBdcCompanyDataEditRequestConsumed = useCallback(() => {
    setBdcCompanyDataEditRequirementId(null);
  }, []);

  // Navigate to proof of address requirement
  const handleNavigateToProofOfAddress = useCallback(() => {
    if (proofOfAddressRequirement) {
      const newIndex = navigationRequirements.findIndex(
        (r) => r.id === proofOfAddressRequirement.id,
      );
      setNavigationDirection(1);
      previousRequirementIndex.current = newIndex;
      dispatch(setActiveRequirement(proofOfAddressRequirement));
      return;
    }
    // Defense in depth: si no hay POA en el subfolder (ej. Contrato de Arrendamiento),
    // refrescar la bóveda para que el sidebar muestre el siguiente requirement disponible
    // en vez de quedarse con pantalla en blanco.
    if (folder?.id) {
      invalidateVaultFolder();
    }
  }, [
    dispatch,
    proofOfAddressRequirement,
    navigationRequirements,
    folder?.id,
    invalidateVaultFolder,
  ]);

  // Navigate to data validation virtual step (after INE address confirmed as updated)
  const handleNavigateToDataValidation = useCallback(
    (options?: { requiresIdentityReview?: boolean }) => {
      // Contact review cannot correct immutable personal fields. Let the caller
      // explain the required authorized review instead of opening the wrong form.
      if (hasApprovedPersonForm && options?.requiresIdentityReview)
        return false;
      // OCR completion navigates internally without options. User correction
      // actions supply options and must respect the current navigation guard.
      if (
        options &&
        navigationHiddenRef.current &&
        !isConfirmingBlockedNavigationRef.current
      ) {
        setPendingNavAction(() => () => {
          handleNavigateToDataValidation(options);
        });
        return true;
      }
      const dataValidationReq = navigationRequirements.find(
        (r) =>
          r.slug === DATA_VALIDATION_SLUG || r.slug === BDC_CONTACT_REVIEW_SLUG,
      );
      if (dataValidationReq) {
        if (options && isRequirementLocked(dataValidationReq)) return false;
        const newIndex = navigationRequirements.findIndex(
          (r) => r.id === dataValidationReq.id,
        );
        setNavigationDirection(1);
        previousRequirementIndex.current = newIndex;
        dispatch(setActiveRequirement(dataValidationReq));
        return true;
      }
      return false;
    },
    [
      dispatch,
      navigationRequirements,
      hasApprovedPersonForm,
      isRequirementLocked,
    ],
  );

  // Options to pass to documentationMap for ID, ProofOfAddress, and general components
  const documentationMapOptions: DocumentationMapOptions = useMemo(
    () => ({
      reviewAddressOnEntry:
        manualIdentityEntry?.folderId === folder.id &&
        manualIdentityEntry.requirementId === activeRequirement?.id &&
        !isProofOfAddressCompleted &&
        !isProofOfAddressProcessing(proofOfAddressRequirement) &&
        !isDataValidationCompleted &&
        !hasApprovedPersonForm,
      onIDValidationComplete: handleIDValidationComplete,
      onIDValidationActivityChange: handleIDValidationActivityChange,
      identityValidationActivity,
      isIDCompleted,
      onNavigateToProofOfAddress: handleNavigateToProofOfAddress,
      onNavigateToDataValidation: handleNavigateToDataValidation,
      onRegisterSubmit: handleRegisterIDSubmit,
      onRegisterProofOfAddressSubmit: handleRegisterProofOfAddressSubmit,
      onRegisterGeneralSubmit: generalSubmitRegistrar,
      onSetNavigationHidden: handleSetNavigationHidden,
      onAddressConfirmationResult: handleAddressConfirmationResult,
      onProofOfAddressComplete: handlePOAValidationComplete,
      canContinueAfterProofOfAddress: canNavigateToNextRequirement,
      onNavigateToRequirement: handleNavigateToRequirement,
      forceBdcCompanyDataEdit:
        activeRequirement?.id === bdcCompanyDataEditRequirementId,
      onBdcCompanyDataEditRequestConsumed:
        handleBdcCompanyDataEditRequestConsumed,
    }),
    [
      activeRequirement?.id,
      folder.id,
      manualIdentityEntry,
      isProofOfAddressCompleted,
      isDataValidationCompleted,
      proofOfAddressRequirement,
      hasApprovedPersonForm,
      bdcCompanyDataEditRequirementId,
      handleIDValidationComplete,
      handleIDValidationActivityChange,
      identityValidationActivity,
      isIDCompleted,
      handleNavigateToProofOfAddress,
      handleNavigateToDataValidation,
      handleNavigateToRequirement,
      handleRegisterIDSubmit,
      handleRegisterProofOfAddressSubmit,
      generalSubmitRegistrar,
      handleSetNavigationHidden,
      handleAddressConfirmationResult,
      handlePOAValidationComplete,
      canNavigateToNextRequirement,
      handleBdcCompanyDataEditRequestConsumed,
    ],
  );

  // Render requirement content
  const renderRequirementContent = (requirement: Requirement) => {
    if (
      bdcRequirement &&
      canReadBdcStatus &&
      (bdcStatusQuery.isPending || bdcReportReplacementRequirementId) &&
      (requirement.slug === DATA_VALIDATION_SLUG ||
        requirement.slug === BDC_CONTACT_REVIEW_SLUG ||
        isBdcCompanyDataRequirement(requirement.slug, requirement.type) ||
        isBdcAuthorizationRequirement(requirement.slug, requirement.type))
    ) {
      return (
        <div role="status" className="py-16 text-center text-slate-600">
          Actualizando autorización de Buró de Crédito…
        </div>
      );
    }
    // Virtual data-validation step renders its own component
    if (requirement.slug === DATA_VALIDATION_SLUG) {
      return (
        <DataValidation
          key={dataValidationScope}
          onCanAdvanceChange={handleDataValidationCanAdvanceChange}
        />
      );
    }
    if (requirement.slug === BDC_CONTACT_REVIEW_SLUG && bdcRequirement) {
      return (
        <BdcContactReview
          key={`${idRequirement?.id ?? "no-identity"}-${bdcRequirement.id}`}
          folder={folder}
          idRequirement={idRequirement}
          bdcRequirement={bdcRequirement}
        />
      );
    }

    const element = documentationMap(
      requirement.slug,
      subfolderSlugMap[requirement.subfolder],
      folder,
      documentationMapOptions,
    );
    return element?.component || null;
  };

  // Get sidebar title and description
  const sidebarTitle =
    subfoldersMapping[currentSlug] || currentSubfolder?.name || "Documentos";
  const sidebarDescription =
    subfolderDescriptions[currentSlug] ||
    "Complete los siguientes requisitos para continuar.";

  // Logo component
  const logo = (
    <img
      src="/logo_multiburo.svg"
      alt="Logotipo de Multiburo"
      className="h-24 w-auto"
    />
  );

  // Get tenant name from metadata
  const tenantName = folder.metadata?.tenant?.full_name;

  // Get actor type label based on current subfolder
  const getActorTypeLabel = (subfolderSlug: string): string | undefined => {
    if (subfolderSlug.includes("tenant")) return "Inquilino";
    if (subfolderSlug.includes("guarantor")) return "Fiador";
    if (subfolderSlug.includes("landlord") || subfolderSlug.includes("owner"))
      return "Propietario";
    if (subfolderSlug.includes("lease-contract")) return "Contrato";
    if (subfolderSlug.includes("collateral")) return "Garantía";
    return undefined;
  };

  const actorTypeLabel = getActorTypeLabel(currentSlug);

  // Sidebar content
  const sidebar = (
    <VaultSidebar
      title={sidebarTitle}
      description={sidebarDescription}
      folioNumber={String(folder.folder_number)}
      tenantName={tenantName}
      actorType={actorTypeLabel}
      folderStatus={folder.status}
      requirements={navigationRequirements}
      activeRequirement={activeRequirement?.id || null}
      onSelectRequirement={handleSelectRequirement}
      isIDCompleted={isIDCompleted}
      isProofOfAddressCompleted={isProofOfAddressCompleted}
      isDataValidationCompleted={isDataValidationCompleted}
      isBdcDestinationReviewed={hasCompletedBdcDestinationReview}
      existingBdcRunRequirementId={existingBdcRunRequirementId}
      legacyCompletedBdcRequirementId={legacyCompletedBdcRequirementId}
      bdcReportReplacementRequirementId={bdcReportReplacementRequirementId}
      bdcAuthoritativeRequirementStatus={bdcAuthoritativeRequirementStatus}
      isAddressConfirmedUpdated={isAddressConfirmedUpdated}
      identityValidationActivity={identityValidationActivity}
      lockedFormSlugs={currentTabConfig?.lockedFormSlugs || null}
      bdcPrerequisiteSlugs={currentTabConfig?.bdcPrerequisiteSlugs || null}
    />
  );

  // Top navigation
  const topNav = (
    <div className="flex items-center overflow-hidden">
      {/* Mobile menu button — fixed width so it doesn't get squeezed */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden ml-2 flex-shrink-0"
        onClick={() => setIsStepSheetOpen(true)}
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Subfolder tabs — min-w-0 allows flex child to shrink below content size */}
      <div className="flex-1 min-w-0">
        <VaultTopNav
          subfolders={subfolders}
          activeTab={currentTab}
          onTabChange={handleTabChange}
        />
      </div>
    </div>
  );

  // Swipe navigation for mobile content area
  const { dragHandlers } = useSwipeNavigation({
    onSwipeLeft: handleNext,
    onSwipeRight: handlePrevious,
    enabled: isMobile && !hideGlobalStepNavigation,
  });

  // Main content with animated transitions
  // TabStepContent animates on both tab changes and step changes
  const content = activeRequirement ? (
    <motion.div {...dragHandlers}>
      <TabStepContent
        tabKey={currentTab}
        stepKey={activeRequirement.id}
        direction={navigationDirection}
      >
        <VaultRequirementScope requirementId={activeRequirement.id}>
          <VaultRequirementContent
            requirement={activeRequirement}
            onPrevious={handlePrevious}
            onNext={handleNext}
            hasPrevious={currentStepIndex > 1}
            hasNext={canNavigateToNextRequirement}
            hideNavigation={hideGlobalStepNavigation}
            isSubmittable={isCurrentRequirementSubmittable}
            submitMode={currentSubmitMode}
            isSubmitting={isSubmitting}
            isForwardDisabled={isForwardDisabled}
            nextTabName={
              isLastRequirement ? nextTabInfo?.displayName : undefined
            }
            onNextTab={isLastRequirement ? handleNextTab : undefined}
            sectionBanner={sectionBanner}
            onDismissBanner={() => setSectionBanner(null)}
          >
            {renderRequirementContent(activeRequirement)}
          </VaultRequirementContent>
        </VaultRequirementScope>
      </TabStepContent>
    </motion.div>
  ) : (
    <VaultEmptyState />
  );

  // Mobile navigation components
  const mobileNavBar = (
    <MobileNavBar
      onPrevious={handlePrevious}
      onNext={handleNext}
      hasPrevious={currentStepIndex > 1}
      hasNext={canNavigateToNextRequirement}
      currentStep={currentStepIndex}
      totalSteps={navigationRequirements.length}
      onOpenStepSheet={() => setIsStepSheetOpen(true)}
      isSubmittable={isCurrentRequirementSubmittable}
      submitMode={currentSubmitMode}
      isSubmitting={isSubmitting}
      isForwardDisabled={isForwardDisabled}
      isHidden={hideGlobalStepNavigation}
      nextTabName={isLastRequirement ? nextTabInfo?.displayName : undefined}
      onNextTab={isLastRequirement ? handleNextTab : undefined}
    />
  );

  const mobileStepSheetEl = (
    <MobileStepSheet
      title={sidebarTitle}
      description={sidebarDescription}
      folioNumber={String(folder.folder_number)}
      tenantName={tenantName}
      actorType={actorTypeLabel}
      folderStatus={folder.status}
      logo={logo}
      requirements={navigationRequirements}
      activeRequirement={activeRequirement?.id || null}
      onSelectRequirement={handleSelectRequirement}
      isIDCompleted={isIDCompleted}
      isProofOfAddressCompleted={isProofOfAddressCompleted}
      isDataValidationCompleted={isDataValidationCompleted}
      isBdcDestinationReviewed={hasCompletedBdcDestinationReview}
      existingBdcRunRequirementId={existingBdcRunRequirementId}
      legacyCompletedBdcRequirementId={legacyCompletedBdcRequirementId}
      bdcReportReplacementRequirementId={bdcReportReplacementRequirementId}
      bdcAuthoritativeRequirementStatus={bdcAuthoritativeRequirementStatus}
      isAddressConfirmedUpdated={isAddressConfirmedUpdated}
      identityValidationActivity={identityValidationActivity}
      lockedFormSlugs={currentTabConfig?.lockedFormSlugs || null}
      bdcPrerequisiteSlugs={currentTabConfig?.bdcPrerequisiteSlugs || null}
      isOpen={isStepSheetOpen}
      onClose={() => setIsStepSheetOpen(false)}
    />
  );

  return (
    <AutoSaveProvider>
      <VaultLayout
        logo={logo}
        sidebar={sidebar}
        topNav={topNav}
        content={content}
        mobileBottomBar={mobileNavBar}
        mobileStepSheet={mobileStepSheetEl}
        contentScrollRef={contentScrollRef}
      />
      <VerificationBlockerModal
        blocker={blocker}
        isBlocking={isNavigationHidden}
        pendingNavAction={pendingNavAction ? runPendingNavAction : null}
        onDismissPendingNav={clearPendingNav}
      />
    </AutoSaveProvider>
  );
}
